import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AccountStateInfo, AccountLifecycleState, SfAccountSnapshot } from '@/types';
import { formatDate } from '@/lib/utils';
import { Clock, Calendar, AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { ScheduleCancellationModal } from './ScheduleCancellationModal';
import { CancelTrialModal } from './CancelTrialModal';
import { UndoCancellationModal } from './UndoCancellationModal';

interface LifecycleCardProps {
  account: SfAccountSnapshot;
  lifecycle: AccountStateInfo;
  onActionComplete: () => void;
}

function getStateStyles(state: AccountLifecycleState): {
  badge: string;
  icon: React.ReactNode;
} {
  switch (state) {
    case 'free_trial':
      return {
        badge: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Clock className="h-4 w-4 text-blue-600" />,
      };
    case 'active':
      return {
        badge: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
      };
    case 'scheduled_to_end':
      return {
        badge: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: <Calendar className="h-4 w-4 text-amber-600" />,
      };
    case 'trial_cancelled':
      return {
        badge: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <AlertCircle className="h-4 w-4 text-orange-600" />,
      };
    case 'ended':
      return {
        badge: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <XCircle className="h-4 w-4 text-gray-600" />,
      };
    default:
      return {
        badge: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <Clock className="h-4 w-4 text-gray-600" />,
      };
  }
}

export function LifecycleCard({ account, lifecycle, onActionComplete }: LifecycleCardProps) {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCancelTrialModal, setShowCancelTrialModal] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);

  const styles = getStateStyles(lifecycle.state);

  const handleActionSuccess = () => {
    setShowScheduleModal(false);
    setShowCancelTrialModal(false);
    setShowUndoModal(false);
    onActionComplete();
  };

  // Determine which actions to show
  const showScheduleCancellation = lifecycle.state === 'active';
  const showCancelTrial = lifecycle.state === 'free_trial';
  const showChangeEndDate = lifecycle.state === 'scheduled_to_end';
  const showUndoCancellation = lifecycle.state === 'scheduled_to_end';

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-abby-primary" />
            Lifecycle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* State Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-500">State:</span>
            <Badge className={`${styles.badge} border`}>
              <span className="flex items-center gap-1.5">
                {styles.icon}
                {lifecycle.displayLabel}
              </span>
            </Badge>
          </div>

          {/* Next Milestone */}
          {lifecycle.nextMilestone && (
            <div>
              <span className="text-sm font-medium text-gray-500">Next milestone:</span>
              <p className="mt-1 text-sm text-gray-900">{lifecycle.nextMilestone}</p>
            </div>
          )}

          {/* Last Synced */}
          <div>
            <span className="text-sm font-medium text-gray-500">Last synced:</span>
            <p className="mt-1 text-sm text-gray-900">{formatDate(account.last_synced_at)}</p>
          </div>

          {/* Action Buttons */}
          <div className="pt-2 space-y-2">
            {/* Active Account - Schedule Cancellation */}
            {showScheduleCancellation && (
              <div>
                <Button
                  onClick={() => setShowScheduleModal(true)}
                  className="w-full"
                >
                  Schedule cancellation
                </Button>
                <p className="mt-1 text-xs text-gray-500 text-center">
                  Service continues until your chosen end date.
                </p>
              </div>
            )}

            {/* Scheduled to End - Change Date and Undo */}
            {showChangeEndDate && (
              <div className="space-y-2">
                <Button
                  onClick={() => setShowScheduleModal(true)}
                  className="w-full"
                >
                  Change end date
                </Button>
                {showUndoCancellation && (
                  <Button
                    variant="outline"
                    onClick={() => setShowUndoModal(true)}
                    className="w-full"
                  >
                    Undo cancellation
                  </Button>
                )}
              </div>
            )}

            {/* Free Trial - Cancel Trial */}
            {showCancelTrial && (
              <div>
                <Button
                  onClick={() => setShowCancelTrialModal(true)}
                  className="w-full"
                >
                  Cancel trial
                </Button>
                <p className="mt-1 text-xs text-gray-500 text-center">
                  Service continues until end of day on the selected date (no charges).
                </p>
              </div>
            )}

            {/* Trial Cancelled - No actions (MVP) */}
            {lifecycle.state === 'trial_cancelled' && (
              <p className="text-sm text-gray-500 text-center py-2">
                No actions available
              </p>
            )}

            {/* Ended - No actions (MVP) */}
            {lifecycle.state === 'ended' && (
              <p className="text-sm text-gray-500 text-center py-2">
                No actions available
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <ScheduleCancellationModal
        open={showScheduleModal}
        onOpenChange={setShowScheduleModal}
        account={account}
        isChangingDate={lifecycle.state === 'scheduled_to_end'}
        onSuccess={handleActionSuccess}
      />

      <CancelTrialModal
        open={showCancelTrialModal}
        onOpenChange={setShowCancelTrialModal}
        account={account}
        onSuccess={handleActionSuccess}
      />

      <UndoCancellationModal
        open={showUndoModal}
        onOpenChange={setShowUndoModal}
        account={account}
        onSuccess={handleActionSuccess}
      />
    </>
  );
}
